import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import MediaQuery from 'react-responsive';
import moment from 'moment-timezone';
import { breakpoints } from 'helpers/breakpoints';
import _ from 'lodash';
import { ascendingSorting } from 'helpers/common';
import styles from './styles-module.css';
import { messages } from './locale';
import TimeslotRow from './row';
import EventsIcon from '../../../assets/events.svg';
import ToolTipIcon from '../../../assets/add-ons/info.svg';
import LoadingSpinner from '../../loading';
import { checkExclusiveRuleMatch, getAvailableScheduleSlots } from './timeslot-utils';

const ListingTimeslot = injectIntl((props) => {
  const { intl, eventData, timeslotList, setTimeslotList, listingIds,
    dataLoader, addOnAvailabilityRule, selectedTicketTypes, listingsFilterSetting } = props;

  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showTimeslotModal, setShowTimeslotModal] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [timeslotRates, setTimeslotRates] = useState([]);
  const [availabilityList, setAvailabilityList] = useState([]);
  const [hasOldCheckout, setHasOldCheckout] = useState([]);
  const [slots, setSlots] = useState([]);
  const listingLength = listingIds.length;

  useEffect(() => {
    if (eventData.length && listingLength === 0) {
      const filteredNewCheckoutEvent = [];
      const filteredOldCheckoutEvent = [];
      eventData.forEach((listing) => {
        if (listing.usingNewCheckout) {
          filteredNewCheckoutEvent.push(listing);
        } else {
          filteredOldCheckoutEvent.push(listing);
        }
      });
      setRowData(filteredNewCheckoutEvent);
      setHasOldCheckout(filteredOldCheckoutEvent);
    }
  }, [eventData, listingLength]);

  useEffect(() => {
    if (listingLength > 0 && eventData.length > 0) {
      const usingNewCheckout = [];
      const usingOldCheckout = [];
      const selectedEvents = eventData.filter(dataObj => listingIds.includes(dataObj.id));
      selectedEvents.forEach((listing) => {
        if (listing.usingNewCheckout) {
          usingNewCheckout.push(listing);
        } else {
          usingOldCheckout.push(listing)
        }
      });
      setRowData(usingNewCheckout); // always show only new checkout events
      setHasOldCheckout(usingOldCheckout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingIds, eventData]);

  useEffect(() => {
    const tempTimeslotList = _.cloneDeep(timeslotList);
    timeslotList.forEach((list, i) => {
      const newRates = [];
      const filterData = eventData.length > 0 && eventData.filter(fData => fData.id === list.listingId);
      if (filterData.length > 0) {
        filterData[0].rates.nodes.forEach(rate => {
          if (selectedTicketTypes
            && Object.keys(selectedTicketTypes).length > 0
            && selectedTicketTypes[list.listingId]
            && Array.from((selectedTicketTypes[list.listingId])).indexOf(rate.id) > -1)
          {
            newRates.push(rate);
          }
        });
        const warning = isRuleMatching(list.startTime,
          list.endDate, list.endTime, filterData[0].timeSlots.nodes, newRates, list.startDate);
        tempTimeslotList[i] = { ...tempTimeslotList[i], timeslotRates:newRates, showWarning:warning };
      }
    });
    setTimeslotList(tempTimeslotList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketTypes]);

  const formatTimeString = (timeData) => {
    return (timeData !== null) && (timeData.toString().length === 1
      ? `0${timeData}`
      : timeData);
  }

  const parsedTimeRange = (timeRange) => {
    const parsedTime = moment(timeRange, ['h:mm A', 'hh:mm A']).format('HH:mm');
    return parsedTime;
  }

  function isRuleMatching(
    startTimeRange,
    endDateRange,
    endTimeRange,
    slotsRange,
    timeslotRatesRange,
    ruleStartDate,
    availableWeekdays = []
  ) {
    let ruleSelectedDays = [];
    const ruleStartTime = startTimeRange ? moment.utc(startTimeRange, 'h:mm A').format('HH:mm') : null;
    const ruleEndTime = endTimeRange ? moment.utc(endTimeRange, 'h:mm A').format('HH:mm') : null;
    const getWeekdays = (weekStartDate, weekEndDate) => {
      return Array.from({ length: moment(weekEndDate).diff(weekStartDate, 'days') + 1 },
      (_week, i) => moment(weekStartDate).add(i, 'days').day());
    }

    const today = moment().startOf('day');
    if (addOnAvailabilityRule.length === 0) {
      if ((startTimeRange === null || startTimeRange === undefined)
        && (selectedWeekDays.length === 7 || selectedWeekDays.length === 0)) return false;
    }

    if (endDateRange && moment(endDateRange).isBefore(today, 'day')) return true;

    const getSchedule = getAvailableScheduleSlots(startTimeRange, endDateRange,
      endTimeRange, slotsRange, ruleStartDate);
    let ruleStartDay = moment(ruleStartDate).isoWeekday();
    if (endDateRange === null) {
      getSchedule.availableSlotDate.forEach(availDate => {
        const dayFound = ruleSelectedDays.some(r => getWeekdays(availDate.slotStart, availDate.slotEnd).includes(r));
        if (!dayFound) {
          ruleSelectedDays = [...ruleSelectedDays, ...getWeekdays(availDate.slotStart, availDate.slotEnd)];
        }
      })
    } else {
      // isoWeekday returns 7 for sunday, our database is maintaing sunday as 0.
      if (ruleStartDay === 7) {
        ruleStartDay = 0;
      }
      ruleSelectedDays = [...ruleSelectedDays, ...getWeekdays(ruleStartDate, endDateRange)];
    }

    const getExclusiveRule = checkExclusiveRuleMatch(timeslotRatesRange, ruleSelectedDays, slotsRange,
      getSchedule.availableSlots, ruleStartTime, ruleEndTime , availableWeekdays, selectedWeekDays);
      if (getSchedule.isScheduleSlotMatch) {
        if (timeslotRatesRange.length > 0
          && (getExclusiveRule.isExclusiveRuleMatch || getExclusiveRule.timeInBetween))
        {
          return false;
        } else if (timeslotRatesRange.length === 0) {
          return false;
        }
      }
      return true;
  }

  const handleTimeslotsSelected = () => {
    const warning = isRuleMatching(startTime, endDate, endTime, slots, timeslotRates, startDate);

    if (editIndex !== null) {
      timeslotList[editIndex].startDate = startDate;
      timeslotList[editIndex].endDate = endDate;
      timeslotList[editIndex].startTime = startTime;
      timeslotList[editIndex].endTime = endTime;
      timeslotList[editIndex].weekdays = ascendingSorting(selectedWeekDays);
      timeslotList[editIndex].timeslotRates = timeslotRates;
      timeslotList[editIndex].showWarning = warning;
      setTimeslotList([...timeslotList]);
    }
    else {
      const list = [{
        listingId: selectedListing,
        startDate,
        endDate,
        startTime,
        endTime,
        weekdays: ascendingSorting(selectedWeekDays),
        addOnAvailabilityRuleId: null,
        showWarning: warning,
        timeslotRates,
      }];

      let sameList = false;
      for (let i = 0; i < timeslotList.length; i+=1) {
        if (timeslotList[i].listingId === list[0].listingId)
        {
          const startTimeStr= moment(timeslotList[i].startTime, 'hh:mm A');
          const endTimeStr= moment(timeslotList[i].endTime, 'hh:mm A');
          if ((moment(timeslotList[i].startDate)).isSame(list[0].startDate)
            && (moment(timeslotList[i].endDate)).isSame(list[0].endDate)
            && ((moment(startTimeStr).isSame(moment(list[0].startTime, 'hh:mm A')))
            || (timeslotList[i].startTime === null && list[0].startTime === null))
            && ((moment(endTimeStr).isSame(moment(list[0].endTime, 'hh:mm A')))
            || (timeslotList[i].endTime === null && list[0].endTime === null))
            && (_.isEqual(timeslotList[i].weekdays, list[0].weekdays))
          ) {
            sameList = true;
            break;
          }
        }
      }
      if (!sameList) {
        setTimeslotList([...list, ...timeslotList]);
      }
    }
    setShowTimeslotModal(false);
  }

  const renderNoEventsFound = () => {
    return (
      <div className={styles.noEventsContainer}>
        <div><EventsIcon /></div>
        <div><span>{intl.formatMessage(messages.noEventsFound)}</span></div>
      </div>
    );
  }

  const renderTimeslotRow = () => {
    return <TimeslotRow
      eventData={rowData}
      intl={intl}
      timeslotList={timeslotList}
      setTimeslotList={setTimeslotList}
      selectedWeekDays={selectedWeekDays}
      setSelectedWeekDays={setSelectedWeekDays}
      startTime={startTime}
      setStartTime={setStartTime}
      endTime={endTime}
      setEndTime={setEndTime}
      startDate={startDate}
      setStartDate={setStartDate}
      endDate={endDate}
      setEndDate={setEndDate}
      selectedListing={selectedListing}
      setSelectedListing={setSelectedListing}
      handleTimeslotsSelected={handleTimeslotsSelected}
      showTimeslotModal={showTimeslotModal}
      setShowTimeslotModal={setShowTimeslotModal}
      setEditIndex={setEditIndex}
      setTimeslotRates={setTimeslotRates}
      timeslotRates={timeslotRates}
      addOnAvailabilityRule={addOnAvailabilityRule}
      availabilityList={availabilityList}
      setAvailabilityList={setAvailabilityList}
      selectedTicketTypes={selectedTicketTypes}
      parsedTimeRange={parsedTimeRange}
      formatTimeString={formatTimeString}
      slots={slots}
      setSlots={setSlots}
      isRuleMatching={isRuleMatching}
    />;
  }

  return (
    <section className={`${styles.form} ${styles.capacityForm}`}>
      <div className={styles.addOnsTitle}>
        <h3>
          {intl.formatMessage(messages.listingTimeslotTitle)}
        </h3>
        <div className={styles.subtitle}>
          {intl.formatMessage(messages.listingTimeslotSubtitle)}
        </div>
      </div>
      {listingIds.length > 0 && hasOldCheckout.length > 0
        && <div className={styles.newCheckoutInfoBox}>
        <ToolTipIcon className={styles.infoIcon} />
        {intl.formatMessage(messages.newCheckoutInfo)}
      </div>}
      <div className={styles.headerTopLine} />

      {dataLoader
        ? <div className={styles.row}>
            <LoadingSpinner className={styles.loadingSpinner} />
          </div>
        : <div>
          {rowData.length > 0 && ((listingsFilterSetting === "ALL")
            || (listingsFilterSetting === "SOME" && listingIds.length > 0)) &&
            <MediaQuery minWidth={breakpoints.largestMobile + 1}>
              <div className={styles.timeslotHeading}>
                <div className={styles.eventName}>
                  <span className={styles.event}>{intl.formatMessage(messages.eventTitle)}</span>
                </div>
                <div className={styles.availability}>
                  <span className={styles.eventAvail}>{intl.formatMessage(messages.eventAvailability)}</span>
                </div>
                <div className={styles.addTimeslotAction} />
              </div>
              <div className={styles.headerTopLine} />
            </MediaQuery>
          }
          {rowData.length > 0 && ((listingsFilterSetting === "ALL")
            || (listingsFilterSetting === "SOME" && listingIds.length > 0))
            ? renderTimeslotRow()
            : (((hasOldCheckout.length === 0 && rowData.length === 0)
            || listingIds.length === 0) ? renderNoEventsFound() : '')
          }
        </div>
      }
    </section>
  );
})

export default ListingTimeslot;
